<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import ReferenceItemCreateModal from './ReferenceItemCreateModal.vue'
import { listReferenceItems } from '../productService'
import type {
  ExpenseReference,
  OperationReference,
  ReferenceItem,
} from '../types'

const props = defineProps<{
  open: boolean
  title: string
  type: 'operation' | 'expense_type'
  eyebrow: string
  searchLabel: string
  searchPlaceholder: string
  emptyMessage: string
  createTitle: string
  createBaseAmountLabel?: string
  pageSize?: number
}>()

const emit = defineEmits<{
  close: []
  select: [item: OperationReference | ExpenseReference]
}>()

const { t } = useI18n()
const client = useApiClient()
const auth = useAuthStore()
const search = ref('')
const items = ref<(OperationReference | ExpenseReference)[]>([])
const loading = ref(false)
const errorMessage = ref('')
const createOpen = ref(false)
const page = ref(1)
const pageSize = 8
const canCreate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const filteredItems = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return items.value
  return items.value.filter((item) =>
    [item.name, item.code]
      .some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / pageSize)))
const visibleItems = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredItems.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleItems.value.map((item) => ({
    id: item.id,
    title: item.name,
    subtitle: item.code || t('engineering.costing.picker.noCode'),
    meta: item.base_amount != null ? `${item.base_amount} · #${item.id}` : `#${item.id}`,
  })),
)
const pageLabel = computed(() =>
  t('engineering.costing.picker.itemsPage', {
    count: filteredItems.value.length,
    page: page.value,
    total: totalPages.value,
  }),
)

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await listReferenceItems(client, props.type)
    items.value = result as (OperationReference | ExpenseReference)[]
    page.value = Math.min(page.value, Math.max(1, Math.ceil(items.value.length / pageSize)))
  } catch (error: unknown) {
    items.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
    void load()
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  page.value = 1
})

function selectOption(option: SmartPickerOption): void {
  const item = visibleItems.value.find((entry) => entry.id === Number(option.id))
  if (item) emit('select', item)
}

function handleCreated(item: ReferenceItem): void {
  items.value = [item, ...items.value.filter((entry) => entry.id !== item.id)]
  createOpen.value = false
  emit('select', item as OperationReference | ExpenseReference)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="eyebrow"
    :search-label="searchLabel"
    :search-placeholder="searchPlaceholder"
    :search-value="search"
    :loading="loading"
    :error-message="errorMessage"
    :empty-message="emptyMessage"
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="pageLabel"
    :close-aria-label="t('engineering.costing.picker.close')"
    :retry-label="t('engineering.costing.picker.retry')"
    :previous-label="t('engineering.costing.picker.previous')"
    :next-label="t('engineering.costing.picker.next')"
    :cancel-label="t('engineering.costing.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #loading>{{ t('engineering.costing.picker.loading') }}</template>
    <template #helper>
      <div class="picker-helper">
        <div>
          <strong>{{ t('engineering.costing.picker.missingTitle') }}</strong>
          <p>{{ t('engineering.costing.picker.missingHelp') }}</p>
        </div>
        <button type="button" :disabled="!canCreate" @click="createOpen = true">
          {{ t('engineering.costing.picker.addNew') }}
        </button>
      </div>
    </template>
  </SmartPickerModal>
  <ReferenceItemCreateModal
    :open="createOpen"
    :title="createTitle"
    :type="type"
    :base-amount-label="createBaseAmountLabel"
    @close="createOpen = false"
    @created="handleCreated"
  />
</template>

<style scoped>
.picker-helper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d7dfdc;
  background: #f7fbff;
}

.picker-helper strong {
  color: #173454;
  font-size: 13px;
}

.picker-helper p {
  margin: 0;
  color: #66726e;
  font-size: 12px;
  line-height: 1.4;
}

.picker-helper button {
  padding: 8px 12px;
  border: 1px solid #b9c5c1;
  color: #285b9a;
  background: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}

.picker-helper button:disabled {
  color: #9aa7a3;
  background: #f3f6f5;
  cursor: default;
}
</style>
